/**
 * ChatMessage — Single message bubble for the Maestro terminal chat
 * MAESTRO:P0D
 *
 * - User messages: right-aligned, zinc-800 bg
 * - Assistant messages: left-aligned, zinc-900 bg with orange left border
 * - Renders StructuredDataCard when structured_data is present
 * - Renders SuggestedFollowups when suggested_followups is non-empty
 * - Shows response_time_ms in muted text for assistant messages (terminal aesthetic)
 * - Renders MaestroFeedbackControls below each assistant bubble
 *
 * Uses font-mono for AI responses (terminal aesthetic).
 */

import { memo } from 'react';
import StructuredDataCard from './StructuredDataCard';
import SuggestedFollowups from './SuggestedFollowups';
import MaestroFeedbackControls from './MaestroFeedbackControls';

/**
 * Format an ISO timestamp to a readable time string.
 */
function formatTime(iso) {
  try {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

/**
 * @param {Object} props
 * @param {Object} props.message - Message object from useMaestroChat
 * @param {(text: string) => void} props.onFollowup - Called when a follow-up chip is clicked
 * @param {boolean} [props.isLatest] - True for the last message (for scroll anchoring)
 * @param {boolean} [props.followupsDisabled] - Pass-through to SuggestedFollowups
 */
function ChatMessage({ message, onFollowup, isLatest = false, followupsDisabled = false }) {
  const isUser = message.role === 'user';
  const time = formatTime(message.created_at);

  return (
    <div
      data-testid={`chat-message-${message.id}`}
      data-role={message.role}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    >
      {/* Bubble wrapper — limit width for readability */}
      <div className={`max-w-[85%] sm:max-w-[75%] ${isUser ? '' : 'flex-1'}`}>
        {/* Message bubble */}
        <div
          className={`
            px-4 py-3 rounded-xl text-sm leading-relaxed
            ${isUser
              ? 'bg-zinc-800 text-zinc-100 rounded-br-sm'
              : 'bg-zinc-900 text-zinc-200 border-l-2 border-orange-500 rounded-bl-sm font-mono'
            }
          `}
          role="article"
          aria-label={`${isUser ? 'Your message' : 'Maestro response'}: ${message.content}`}
        >
          {/* Message text — preserve line breaks */}
          <p className="whitespace-pre-wrap break-words">{message.content}</p>

          {/* Structured data card (assistant only) */}
          {!isUser && message.structured_data && (
            <StructuredDataCard data={message.structured_data} />
          )}
        </div>

        {/* Meta row: time + response latency */}
        <div className={`flex items-center gap-3 mt-1 px-1 ${isUser ? 'justify-end' : 'justify-start'}`}>
          {time && (
            <span className="text-[10px] text-zinc-600 font-mono">{time}</span>
          )}
          {!isUser && message.response_time_ms != null && (
            <span className="text-[10px] text-zinc-700 font-mono">
              {message.response_time_ms}ms
            </span>
          )}
        </div>

        {/* Suggested follow-ups (assistant only, latest message) */}
        {!isUser && isLatest && message.suggested_followups?.length > 0 && (
          <SuggestedFollowups
            suggestions={message.suggested_followups}
            onSelect={onFollowup}
            disabled={followupsDisabled}
          />
        )}

        {/* Feedback controls (assistant only) */}
        {!isUser && (
          <MaestroFeedbackControls messageId={message.id} />
        )}
      </div>
    </div>
  );
}

export default memo(ChatMessage);
